import React, { useCallback, useEffect, useState } from 'react';
import css from './FilterSidebar.module.css';
import FilterTalentSidebar from './FilterTalentSidebar';
import BrandFilterSidebar from './BrandFilterSidebar';
import GroupFilterSidebar from './GroupFilterSidebar';
import AgencyFilterSidebar from './AgencyFilterSidebar';
import { PARAM_KEYS, SearchPageCategoryParams, UserType } from '../../../util/enums';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { userSelector } from '../../../ducks/user.duck';
import { getAssociatedListingCategory, getUserType } from '../../../util/userDataExtraction';
import { FormattedMessage, useIntl } from 'react-intl';
import { KEYWORDS } from '../../../util/constants';
import {
  loadActiveCategoryFromSession,
  removeActiveCategoryFromSession,
  saveActiveCategoryToSession,
} from '../../../util/searchPageHelpers';
import { Icons } from '../../../components';
import Drawer from 'react-modern-drawer';
import '../../../styles/react-modern-drawer.css';
import { getCategoryFromLocation, isArrayLength } from '../../../util/genericHelpers';

const FilterCategories = ({ config, onHandleSearchChange, onHandleChildClick, viewport }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobileLayout = viewport && viewport.width > 0 && viewport.width < 768;

  const toggleDrawer = () => {
    setIsDrawerOpen(prevState => !prevState);
  };
  const handleClick = () => {
    // Trigger the parent's method via the passed prop
    onHandleChildClick();
  };

  const location = useLocation();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const currentUser = useSelector(userSelector);
  const userType = getUserType(currentUser);
  const associatedListingCategory = getAssociatedListingCategory(currentUser);
  const searchParams = new URLSearchParams(location.search);
  const activeCategoryFromSession = loadActiveCategoryFromSession();
  const listingCategory =
    activeCategoryFromSession ?? searchParams.get(PARAM_KEYS.ListingCategory) ?? UserType.TALENT;
  const [activeCategory, setActiveCategory] = useState(listingCategory);
  const searchParamsListingCategory = searchParams.get(PARAM_KEYS.ListingCategory);

  useEffect(() => {
    if (searchParamsListingCategory) {
      setActiveCategory(searchParamsListingCategory);
    }
  }, [searchParamsListingCategory]);

  // Utility function to update URL parameters
  const updateURLParams = useCallback(
    (paramKey, values) => {
      const activeCategoryFromSession = loadActiveCategoryFromSession() ?? listingCategory;
      // Define allowed parameters for each category
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(KEYWORDS);

      // Update the specific parameter
      if (values.length > 0) {
        searchParams.set(paramKey, values.join(','));
      } else {
        searchParams.delete(paramKey);
      }

      // Set the active category
      if (activeCategoryFromSession) {
        searchParams.set('pub_listingCategory', activeCategoryFromSession);

        // Get allowed parameters for the active category
        const allowedParams =
          SearchPageCategoryParams[activeCategoryFromSession.toLowerCase()] || [];

        // Remove parameters that are not allowed for the current category
        const currentParams = Array.from(searchParams.keys());
        currentParams.forEach(param => {
          // Keep pub_listingCategory and the parameter we're updating
          if (param !== 'pub_listingCategory' && param !== paramKey) {
            // Remove if not in allowed parameters for this category
            if (!allowedParams.includes(param)) {
              searchParams.delete(param);
            }
          }
        });
      }

      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [location, history]
  );

  const updateMultipleURLParams = paramsObj => {
    const activeCategoryFromSession = loadActiveCategoryFromSession() ?? listingCategory;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(KEYWORDS);

    // Update the specific parameters
    Object.keys(paramsObj).forEach(key => {
      const values = paramsObj[key];
      if (values && values.length > 0) {
        searchParams.set(key, values.join(','));
      } else {
        searchParams.delete(key);
      }
    });

    // Set the active category
    if (activeCategoryFromSession) {
      searchParams.set('pub_listingCategory', activeCategoryFromSession);

      // Get allowed parameters for the active category
      const allowedParams = SearchPageCategoryParams[activeCategoryFromSession.toLowerCase()] || [];
      const paramsBeingUpdated = Object.keys(paramsObj);

      // Remove parameters that are not allowed for the current category
      const currentParams = Array.from(searchParams.keys());
      currentParams.forEach(param => {
        // Keep pub_listingCategory and parameters being updated
        if (param !== 'pub_listingCategory' && !paramsBeingUpdated.includes(param)) {
          // Remove if not in allowed parameters for this category
          if (!allowedParams.includes(param)) {
            searchParams.delete(param);
          }
        }
      });
    }
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleCheckboxChange = useCallback(
    (e, paramKey) => {
      const { checked, value, name } = e.target;
      const skipLowerCase = [
        'conference',
        'division',
        'school',
        'agencyText',
        'industry',
        'brandPremier',
        'state',
        'customInterests',
      ].includes(name);
      const lowerValue = skipLowerCase ? value : value.toLowerCase();
      const searchParams = new URLSearchParams(location.search);
      const existing = searchParams.get(paramKey);
      let values = existing ? existing.split(',') : [];
      if (checked) {
        if (!values.includes(lowerValue)) {
          values.push(lowerValue);
        }
      } else {
        values = values.filter(v => v !== lowerValue);
      }
      updateURLParams(paramKey, values);
    },
    [location, updateURLParams]
  );
  const resetAllFilters = () => {
    try {
      const searchParams = new URLSearchParams(location.search);

      // Remove all except ListingCategory
      for (const key of Object.values(PARAM_KEYS)) {
        if (key !== PARAM_KEYS.ListingCategory) {
          searchParams.delete(key);
          removeActiveCategoryFromSession();
        }
      }

      const queryString = searchParams.toString();
      history.push({
        pathname: location.pathname,
        search: queryString ? `?${queryString}` : '',
      });
    } catch (error) {
      console.error('Failed to reset filters', error);
    }
  };

  const handleClearFilter = useCallback(
    paramKey => {
      updateURLParams(paramKey, []);
    },
    [updateURLParams]
  );
  const handleRadioChange = useCallback(
    (e, paramKey) => {
      const { value } = e.target;
      // Update URL params with the selected age range value.
      updateURLParams(paramKey, [value]);
    },
    [updateURLParams]
  );

  const commonProps = {
    config,
    history,
    location,
    routeConfiguration,
    activeCategory,
    resetAllFilters,
    handleCheckboxChange,
    updateURLParams,
    updateMultipleURLParams,
    handleClearFilter,
    handleRadioChange,
    userType,
    onHandleSearchChange,
    intl,
    associatedListingCategory,
  };

  // Define categories and move them inside the component so we can use the config prop.
  const baseCategories = [
    {
      key: UserType.TALENT,
      label: 'Talent',
      content: <FilterTalentSidebar {...commonProps} />,
    },
    {
      key: UserType.BRAND,
      label: 'Brand',
      content: <BrandFilterSidebar {...commonProps} />,
    },
    {
      key: UserType.SCHOOL,
      label: 'Group',
      content: <GroupFilterSidebar {...commonProps} />,
    },
  ];

  const categories = [getCategoryFromLocation(location, baseCategories)];

  const toggleCategory = categoryKey => {
    const newActiveCategory = activeCategory === categoryKey ? null : categoryKey;
    setActiveCategory(newActiveCategory);
    saveActiveCategoryToSession(newActiveCategory);
  };

  return (
    <>
      {isMobileLayout ? (
        <div className={css.filterCategories}>
          <div className={css.filterHead} onClick={toggleDrawer}>
            <span className={css.filterHeading}>
              <FormattedMessage id="FilterCategories.filter" />
              <Icons name="filter" />
            </span>
          </div>

          <Drawer
            className={css.filterDrawer}
            open={isDrawerOpen}
            onClose={toggleDrawer}
            direction="left"
            lockBackgroundScroll={true}
          >
            <div className={css.mobileDrawerContent}>
              <div className={css.filterCategories}>
                {isArrayLength(categories) &&
                  categories?.map(category => (
                    <div key={category?.key} className={css.category}>
                      <div
                        className={classNames(css.categoryHeader, {
                          [css.activeCategory]: activeCategory === category?.key,
                        })}
                        onClick={() => toggleCategory(category?.key)}
                      >
                        <h3 className={css.categoryTitle}>{category?.label}</h3>
                        <div className={css.categoryClearSec}>
                          <span className={css.clearBtn} onClick={resetAllFilters}>
                            <FormattedMessage id="FilterCategories.clear" />
                          </span>
                          <span className={css.toggleIcon}>
                            {activeCategory === category?.key ? (
                              <Icons name="upAngle" />
                            ) : (
                              <Icons name="downAngle" />
                            )}
                          </span>
                        </div>
                      </div>
                      {activeCategory === category?.key && (
                        <div className={css.categoryContent}>{category?.content}</div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </Drawer>
        </div>
      ) : (
        <div className={css.filterCategories}>
          <div className={css.filterHead} onClick={handleClick}>
            <span className={css.filterHeading}>
              <Icons name="filter" /> <FormattedMessage id="FilterCategories.filter" />
            </span>
            <span className={css.expandCollapse}>
              <Icons name="leftAngle" />
            </span>
          </div>
          {isArrayLength(categories) &&
            categories?.map(category => (
              <div key={category?.key} className={css.category}>
                <div
                  className={classNames(css.categoryHeader, {
                    [css.activeCategory]: activeCategory === category?.key,
                  })}
                  onClick={() => toggleCategory(category?.key)}
                >
                  <h3 className={css.categoryTitle}>{category?.label}</h3>
                  <div className={css.categoryClearSec}>
                    <span className={css.clearBtn} onClick={resetAllFilters}>
                      <FormattedMessage id="FilterCategories.clear" />
                    </span>
                    <span className={css.toggleIcon}>
                      {activeCategory === category?.key ? (
                        <Icons name="upAngle" />
                      ) : (
                        <Icons name="downAngle" />
                      )}
                    </span>
                  </div>
                </div>
                {activeCategory === category?.key && (
                  <div className={css.categoryContent}>{category?.content}</div>
                )}
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default FilterCategories;
